<!-- Korvi lisamise teate komponent -->
<template>
  <v-btn class="cart-btn" color="eraklient" type="button" @click.stop.prevent="openAddToCartDialog">{{ $t('lisa_korvi') }}</v-btn>
  <v-dialog
      v-model="clicked"
      :max-width="420"
      transition="dialog-bottom-transition"
      persistent
  >
    <v-card class="confirmation-dialog-card">
      <v-toolbar color="white" :title="$t('kinnitus')">
        <v-btn icon="mdi-close" @click.stop="clicked = false"></v-btn>
      </v-toolbar>
      <v-card-text class="pa-4 pt-3">
        <div class="confirmation-header">
          <span class="confirmation-kicker">{{ $t('kinnitus_conf') }}</span>
        </div>
        <div class="confirmation-summary">
          <div class="summary-product-chip">{{ getHeader(product) }}</div>
          <div class="summary-topline">
            <span class="summary-label">{{ $t('valige_kogus') }}</span>
            <span class="summary-chip summary-chip--count">{{ quantity }}</span>
          </div>
          <div v-if="hasSprayRollOnOption(product) || hasBottleSizeOption(product)" class="summary-meta">
            <div v-if="hasSprayRollOnOption(product)" class="summary-row">
              <span class="summary-label">{{ $t('lohna_tuup') }}</span>
              <span class="summary-chip">{{ getChoiceLabel() }}</span>
            </div>
            <div v-if="hasBottleSizeOption(product)" class="summary-row">
              <span class="summary-label">{{ $t('vali_pudeli_suurus') }}</span>
              <span class="summary-chip">{{ getSizeLabel() }}</span>
            </div>
          </div>
          <div class="summary-price-pill summary-price-pill--bottom">
            <span class="summary-price-value">{{ formatPrice(getDialogTotal(product, size)) }}</span>
            <span class="summary-price-currency">EUR</span>
          </div>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <div class="pa-4 pt-3">
        <div class="purchase-config">
          <div class="config-card">
            <div class="config-label">{{ $t('valige_kogus') }}</div>
            <div class="quantity-picker">
              <v-btn color="eraklient" @click.stop="decreaseQuantity" icon="mdi-minus" size="30px" aria-label="Decrease Quantity"><v-icon size="20px"></v-icon></v-btn>
              <div class="quantity-input-wrap">
                <input class="quantity-input" :title="$t('valige_kogus')" v-model.number="quantity" type="text" inputmode="numeric" pattern="[0-9]*">
              </div>
              <v-btn color="eraklient" @click.stop="increaseQuantity" icon="mdi-plus" size="30px" aria-label="Increase Quantity"><v-icon size="20px"></v-icon></v-btn>
            </div>
          </div>
          <div v-if="hasSprayRollOnOption(product)" class="config-card">
            <div class="config-label">{{ $t('lohna_tuup') }}</div>
            <div class="option-toggle" role="radiogroup" :aria-label="$t('lohna_tuup')">
              <button type="button" :class="['option-pill', { 'option-pill--active': choice === 'Spray', 'option-pill--disabled': !isSprayAvailable(product) }]" :aria-pressed="choice === 'Spray'" :disabled="!isSprayAvailable(product)" @click="!isSprayAvailable(product) ? null : (choice = 'Spray', choiceError = false)">
                Spray
              </button>
              <button type="button" :class="['option-pill', { 'option-pill--active': choice === 'Roll-on', 'option-pill--disabled': !isRollOnAvailable(product) }]" :aria-pressed="choice === 'Roll-on'" :disabled="!isRollOnAvailable(product)" @click="!isRollOnAvailable(product) ? null : (choice = 'Roll-on', choiceError = false)">
                Roll-on
              </button>
            </div>
            <div v-if="choiceError" class="config-error">{{ $t('vali_lohna_tuup_error') }}</div>
          </div>
          <div v-if="hasBottleSizeOption(product)" class="config-card">
            <div class="config-label">{{ $t('vali_pudeli_suurus') }}</div>
            <div class="option-toggle" role="radiogroup" :aria-label="$t('vali_pudeli_suurus')">
              <button
                v-for="bs in getBottleSizes(product)"
                :key="bs.size"
                type="button"
                :class="['option-pill', { 'option-pill--active': size === bs.size, 'option-pill--disabled': bs.disabled }]"
                :aria-pressed="size === bs.size"
                :disabled="bs.disabled"
                @click="bs.disabled ? null : selectBottleSize(bs.size)"
              >{{ bs.size }}</button>
            </div>
            <div v-if="bottleSizeError" class="config-error">{{ $t('vali_pudeli_suurus_error') }}</div>
          </div>
        </div>
      </div>
      <div class="pa-4">
        <v-btn class="product-cart-btn" color="eraklient" block type="button" @click.stop.prevent="confirmAddToCart(product)">{{ $t('lisa_korvi') }}</v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import {toast} from "vue3-toastify";
import { formatPriceValue, getProductUnitPrice, isBottleSizeOptionEnabled, isSprayAvailable, isRollOnAvailable, isSprayRollOnOptionEnabled, parseBottleSizes } from "@/utils/productOptions.js";

export default {
  name: "AddToCart",
  props: ['product'],
  emits: ['dialog-visibility-change'],
  data() {
    return {
      clicked: false,
      quantity: 1,
      choice: null,
      size: null,
      bottleSizeError: false,
      choiceError: false,
    };
  },
  watch: {
    clicked(value) {
      if (value) {
        this.choice = this.hasSprayRollOnOption(this.product) ? this.choice : null;
      }

      if (!value) {
        this.bottleSizeError = false;
        this.choiceError = false;
      }

      this.$emit('dialog-visibility-change', value);
    }
  },
  beforeUnmount() {
    this.$emit('dialog-visibility-change', false);
  },
  methods: {
    openAddToCartDialog() {
      this.clicked = true;
    },
    increaseQuantity() {
      this.quantity++;
    },
    decreaseQuantity() {
      if (this.quantity > 1) {
        this.quantity--;
      }
    },
    addToCart(product) {
      const choice = this.hasSprayRollOnOption(product) ? this.choice : null;
      const size = this.hasBottleSizeOption(product) ? this.size : null;

      if (this.hasSprayRollOnOption(product) && !choice) {
        this.choiceError = true;
        return false;
      }

      if (this.hasBottleSizeOption(product) && !size) {
        this.bottleSizeError = true;
        return false;
      }

      this.$store.dispatch('addToCart', { product: product, quantity: this.quantity, choice, size });
      this.quantity = 1;
      this.choice = null;
      this.size = null;
      this.bottleSizeError = false;
      this.choiceError = false;
      return true;
    },
    confirmAddToCart(product) {
      const added = this.addToCart(product);
      if (added) {
        this.clicked = false;
        this.showToastSuccess(this.$t('lisatud_korvi'));
      }
    },
    hasSprayRollOnOption(content) {
      return isSprayRollOnOptionEnabled(content);
    },
    isSprayAvailable(content) {
      return isSprayAvailable(content);
    },
    isRollOnAvailable(content) {
      return isRollOnAvailable(content);
    },
    hasBottleSizeOption(content) {
      return isBottleSizeOptionEnabled(content);
    },
    getBottleSizes(content) {
      return parseBottleSizes(content);
    },
    getChoiceLabel() {
      return this.choice || 'Spray';
    },
    getSizeLabel() {
      return this.size || '-';
    },
    selectBottleSize(size) {
      this.size = size;
      this.bottleSizeError = false;
    },
    getDialogTotal(content, size) {
      return getProductUnitPrice(content, size) * Number(this.quantity || 0);
    },
    formatPrice(value) {
      return formatPriceValue(value);
    },
    showToastSuccess(value) {
      toast.success(value, {
        autoClose: 3000
      })
    },
    showToastError(value) {
      toast.error(value, {
        autoClose: 3000
      })
    },
    getHeader(content) {
      return (this.$i18n.locale === 'en' && content.header_en !== null) ? content.header_en : (this.$i18n.locale === 'ru' && content.header_ru !== null) ? content.header_ru : content.header;
    }
  }
}
</script>

<style scoped>
.cart-btn {
  width: 100%;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
}

.prod-item:hover .cart-btn {
  visibility: visible;
  opacity: 1;
}

.confirmation-dialog-card {
  border-radius: 22px !important;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.18) !important;
}

.confirmation-dialog-card :deep(.v-toolbar) {
  padding-inline: 6px;
}

.confirmation-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
}

.confirmation-kicker {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5d6b7a;
}

.confirmation-summary {
  display: grid;
  gap: 10px;
  padding: 14px;
  border-radius: 16px;
  background: linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%);
  border: 1px solid rgba(59, 113, 247, 0.14);
}

.summary-product-chip {
  width: 100%;
  padding: 2px 2px 6px;
  font-size: 1.02rem;
  font-weight: 700;
  line-height: 1.35;
  color: #111827;
}

.summary-topline {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.summary-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.summary-label {
  flex: 1 1 auto;
  font-size: 0.86rem;
  color: #5d6b7a;
}

.summary-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: #ffffff;
  color: #111827;
  border: 1px solid rgba(17, 24, 39, 0.08);
  font-size: 0.86rem;
  font-weight: 700;
  justify-self: end;
}

.summary-chip--count {
  min-width: 40px;
  min-height: 40px;
  padding: 0 14px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
}

.summary-price-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 10px 18px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
}

.summary-price-value {
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1;
  color: #111827;
}

.summary-price-currency {
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(59, 113, 247, 0.08);
  color: #3b71f7;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.summary-meta {
  display: grid;
  gap: 10px;
  padding-top: 4px;
}

.summary-price-pill--bottom {
  margin-top: 4px;
}

.purchase-config {
  display: grid;
  gap: 12px;
}

.config-card {
  display: grid;
  gap: 8px;
}

.config-label {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5d6b7a;
}

.quantity-picker {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.quantity-input-wrap {
  width: 48px;
  min-width: 48px;
  max-width: 48px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.05);
  flex: 0 0 48px;
}

.quantity-input {
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
  outline: none;
}

.option-toggle {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.option-pill {
  min-width: 92px;
  min-height: 38px;
  padding: 8px 14px;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 999px;
  background: #ffffff;
  color: #1f2d3d;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.05);
  transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease, color 160ms ease;
}

.option-pill:hover {
  transform: translateY(-1px);
  border-color: rgba(59, 113, 247, 0.35);
}

.option-pill--active {
  background: linear-gradient(135deg, #3b71f7 0%, #5b8cff 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(59, 113, 247, 0.2);
}

.option-pill--disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.config-error {
  margin-top: 2px;
  color: #d32f2f;
  font-size: 0.82rem;
  font-weight: 600;
}

.product-cart-btn {
  border-radius: 999px !important;
  background: linear-gradient(135deg, #3b71f7 0%, #5b8cff 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 10px 20px rgba(59, 113, 247, 0.2) !important;
}

.product-cart-btn:hover {
  transform: translateY(-1px);
}

.product-cart-btn :deep(.v-btn__content) {
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: uppercase;
}

.product-cart-btn :deep(.v-btn__overlay) {
  opacity: 0 !important;
}

@media (max-width: 1000px) {
  .cart-btn {
    visibility: visible;
    opacity: 1;
    margin: 20px 0;
  }

  .summary-price-pill {
    min-height: 44px;
    padding-inline: 14px;
  }

  .summary-price-value {
    font-size: 1.1rem;
  }

  .option-pill {
    flex: 0 1 auto;
  }
}

</style>